import {
    filter,
    find,
    findIndex,
    includes,
    isArray,
    isEmpty,
    map,
    remove,
    startsWith,
    differenceBy
} from 'lodash';

import { formatPrice } from '~/utilities/products';
import { getAddOnBellsCost } from '~/utilities/bells';
import { validatePromoCode } from '~/utilities/promos';
import {
    getBogoItems,
    getTotals,
    queryCartItem,
    createGtagEcommEvent
} from '~/utilities/cart';
import washes from '~/account/api/washes';
import { isLoggedIn, getAccessToken } from '~/account/utilities/helpers';
import { getUser } from '~/context/UserContext';

import persistentCart from '../account/api/persistentCart';

const CART_STORAGE_KEY = 'ab_cart';

function uuidV4() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (
            c ^
            (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16)
    );
}

function toPricing({ subTotal, tax, shipping, discount, grandTotal }) {
    const toCents = (value) =>
        Math.max(0, Math.round((Number(value) || 0) * 100));
    return {
        subtotal_cents: toCents(subTotal),
        tax_cents: toCents(tax),
        shipping_cents: toCents(shipping),
        discount_cents: toCents(discount),
        total_cents: toCents(grandTotal)
    };
}

function cleanObject(obj) {
    if (!obj) return undefined;
    const copy = { ...obj };
    Object.keys(copy).forEach((k) => copy[k] === undefined && delete copy[k]);
    return Object.keys(copy).length ? copy : undefined;
}

function validateCartItem(item) {
    if (!item.id && !item.databaseId) {
        throw new Error('Cart item missing required field: id');
    }
    if (!item.title || typeof item.title !== 'string') {
        throw new Error(
            'Cart item missing required field: title (non-empty string)'
        );
    }
    if (typeof item.price !== 'number' || item.price < 0) {
        throw new Error(
            'Cart item missing required field: price (integer >= 0)'
        );
    }
    if (
        !item.quantity ||
        typeof item.quantity !== 'number' ||
        item.quantity < 1
    ) {
        throw new Error(
            'Cart item missing required field: quantity (integer >= 1)'
        );
    }
    if (!item.cartItemId) {
        throw new Error('Cart item missing required field: cartItemId');
    }
    if (!item.productType || typeof item.productType !== 'string') {
        throw new Error(
            'Cart item missing required field: productType (non-empty string)'
        );
    }
}

export function prepareItemsForManager(items) {
    const toCents = (value) =>
        Math.max(0, Math.round((Number(value) || 0) * 100));

    return items.map((item) => {
        validateCartItem(item);

        const managerItem = {
            id: item.id || item.databaseId,
            title: item.title,
            price: toCents(item.price),
            quantity: item.quantity,
            cartItemId: item.cartItemId,
            productType: item.productType
        };

        if (item.serviceId) managerItem.serviceId = item.serviceId;
        if (item.location) managerItem.location = item.location;
        if (item.image) managerItem.image = item.image;
        if (item.stripe_id) managerItem.stripe_id = item.stripe_id;
        if (item.stripe_price_id)
            managerItem.stripe_price_id = item.stripe_price_id;
        if (item.price_id) managerItem.price_id = item.price_id;
        if (item.subscriptionPlanId)
            managerItem.subscriptionPlanId = item.subscriptionPlanId;
        if (item.billingFrequencyId)
            managerItem.billingFrequencyId = item.billingFrequencyId;
        if (item.pricingGroupId)
            managerItem.pricingGroupId = item.pricingGroupId;
        if (item.vehicle) managerItem.vehicle = item.vehicle;
        if (item.isPhysicalGiftCard !== undefined)
            managerItem.isPhysicalGiftCard = item.isPhysicalGiftCard;
        if (item.giftCardValue !== undefined)
            managerItem.giftCardValue = item.giftCardValue;
        if (item.showVehicleInformationForm !== undefined)
            managerItem.showVehicleInformationForm =
                item.showVehicleInformationForm;
        if (item.addOns && Array.isArray(item.addOns)) {
            managerItem.addOns = item.addOns.map((addOn) => ({
                id: addOn.id,
                serviceId: addOn.serviceId,
                title: addOn.title,
                price: toCents(addOn.price),
                useBells: addOn.useBells,
                bellsCost: addOn.bellsCost ?? null
            }));
        }

        return managerItem;
    });
}

/**
 * Normalize cart API error responses into a consistent shape.
 *
 * @typedef {Object} NormalizedCartError
 * @property {string} type - Error type identifier (e.g. 'not_found', 'validation_error', 'internal_error').
 * @property {number} status - HTTP status code (default: 500).
 * @property {string} message - Human-readable error message.
 * @property {Object} details - Additional structured details from the server.
 * @property {string|null} requestId - Server request identifier for tracing.
 * @property {string} timestamp - ISO8601 timestamp.
 *
 * @param {Object} [response] - Network response object (may contain .status).
 * @param {Object} [body] - Parsed JSON response body with { error: { type, message, details }, request_id, timestamp }.
 * @returns {NormalizedCartError} Normalized error object with defaults applied.
 */
export const normalizeCartError = (response, body) => {
    const status = response?.status || body?.status || 500;
    const errorData = body?.error || {};

    return {
        type: errorData.type || 'internal_error',
        status,
        message: errorData.message || 'An unexpected error occurred',
        details: errorData.details || {},
        requestId: body?.request_id || null,
        timestamp: body?.timestamp || new Date().toISOString()
    };
};

/**
 * CHANGED: New helper to clear cart linkage while preserving items and other data.
 * Clears cart_id, cart_token, and identified flag from localStorage.
 * Preserves items, shippingInfo, discounts, and customerInfo.
 */
export const clearCartStorage = () => {
    try {
        const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '{}');

        cart.cart_id = null;
        cart.cart_token = null;
        cart.identified = false;

        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
        console.error('[Cart] Failed to clear cart storage:', error);
    }
};

/**
 * CHANGED: Completely refactored to return a structured action descriptor.
 * Handle cart errors in a centralized, non-blocking way using the new backend error contract.
 *
 * @typedef {Object} CartErrorActionDescriptor
 * @property {'clear'|'refetch'|'refetch_cart'|'rehydrate'|'retry'|'none'} action - What action to take.
 *   - 'clear'        : Clear local cart linkage (cart_id/cart_token/identified), optionally preserving items.
 *   - 'refetch'      : Refetch server cart metadata (e.g. linkage, items_hash) and optionally allow a retry.
 *   - 'refetch_cart' : Instruct caller to refetch a specific cart from the backend using provided refetchData.
 *   - 'rehydrate'    : Attempt JWT identity rehydrate flow using provided rehydrateData (email + jwtToken).
 *   - 'retry'        : Retry the exact operation (used for idempotent retriable failures).
 *   - 'none'         : No automatic action; caller may show an error to the user.
 * @property {boolean} shouldNotify - Whether to show a user notification.
 * @property {string|null} userMessage - User-facing error message.
 * @property {number} [retryAfter] - Seconds to wait before retry (for rate limits).
 * @property {boolean} [fieldError] - Whether this is a field validation error.
 * @property {boolean} [requiresReauth] - Whether user needs to re-authenticate.
 * @property {boolean} [isRateLimit] - Whether this is a rate limit error.
 * @property {boolean} [isServerError] - Whether this is a server error.
 * @property {boolean} [devError] - Whether this is a developer/integration error.
 * @property {string} [currentItemsHash] - Current items hash from server (for concurrency conflicts).
 * @property {boolean} [allowRetry] - Whether caller can retry after refetch.
 * @property {boolean} [preserveItems] - When true, preserve local items when clearing linkage.
 * @property {Object} [refetchData] - Data to perform a targeted refetch (e.g. { cart_id }).
 * @property {Object} [rehydrateData] - Data to perform JWT rehydrate (e.g. { email, jwtToken }).
 *
 * @param {Object} error - Normalized error object from normalizeCartError.
 * @param {string} context - Operation context (e.g. 'addItemToCart', 'syncCartToStorage/update').
 * @param {Object} [options] - Optional configuration (isLoggedIn, userEmail, jwtToken).
 * @returns {CartErrorActionDescriptor} Action descriptor for the caller to handle.
 */
export const handleCartError = (error, context, options = {}) => {
    const logMessage = [
        `[Cart Error - ${context}]`,
        `Type: ${error.type}`,
        `Status: ${error.status}`,
        `Message: ${error.message}`,
        error.requestId ? `Request ID: ${error.requestId}` : null,
        `Timestamp: ${error.timestamp}`
    ]
        .filter(Boolean)
        .join(' | ');

    console.error(logMessage, error.details);

    const msg = error.message || '';
    const details = error.details || {};

    switch (error.type) {
        case 'validation_error':
            if (
                msg.includes('Invalid cart_id format') ||
                msg.includes('Missing required query parameter: cart_id') ||
                msg.includes('Missing or invalid required field: cart_id')
            ) {
                clearCartStorage();
                return {
                    action: 'clear',
                    shouldNotify: false,
                    userMessage:
                        'Your cart was reset due to an error. Please try again.'
                };
            } else {
                return {
                    action: 'none',
                    shouldNotify: true,
                    fieldError: true,
                    userMessage: msg || 'Please check your input and try again.'
                };
            }

        case 'not_found':
            clearCartStorage();
            return {
                action: 'clear',
                shouldNotify: false,
                userMessage:
                    "Your previous cart could not be found. We've started a new cart."
            };

        case 'conflict':
            // Terminal state conflicts (cart already purchased/closed/expired) should clear linkage
            // and NOT notify the user to avoid alarm; we start a fresh cart silently.
            if (
                msg.includes('terminal state') ||
                msg.includes('Cannot mark a closed cart') ||
                msg.includes(
                    'Cannot update email for cart in terminal state'
                ) ||
                [
                    'PURCHASED',
                    'CLOSED',
                    'EXPIRED',
                    'PURCHASED_NO_SEQUENCE'
                ].includes(
                    // tolerate either string or undefined by coercing to string
                    String(details.state)
                )
            ) {
                clearCartStorage();
                return {
                    action: 'clear',
                    shouldNotify: false,
                    userMessage:
                        "Your previous cart has been completed or expired. We've started a new one."
                };
            } else if (msg.includes('Concurrency conflict')) {
                // Concurrency conflicts should prompt a refetch; keep notifications off by default.
                return {
                    action: 'refetch',
                    shouldNotify: false,
                    userMessage: null,
                    currentItemsHash: details.current_items_hash || null,
                    allowRetry: true
                };
            } else if (msg.includes('Email already set')) {
                // When email is already set on server-side cart, clear linkage silently.
                // We're automatically creating a fresh cart for the user, so avoid notifying them.
                clearCartStorage();
                // Helpful dev log for tracing automatic cart reset events
                console.info(
                    '[Cart] Email already set on server cart; cleared local linkage and started a new cart.'
                );
                return {
                    action: 'clear',
                    shouldNotify: false,
                    userMessage: 'Starting a fresh cart for your account.'
                };
            }
            break;

        case 'authentication_error':
            // Case 1: User's AMP token (login session) is invalid/expired
            if (msg.includes('Invalid or expired AMP token')) {
                // Don't clear cart storage! Keep cart_id and cart_token so it can be
                // relinked after user logs back in via handleLoginCartSync
                return {
                    action: 'none',
                    shouldNotify: false,
                    requiresReauth: true,
                    userMessage: null
                };
            }

            // Case 2: Cart token validation failed (cart_token is invalid)
            // This is a cart-level auth issue, not user-level
            if (msg.includes('Token validation failed')) {
                clearCartStorage();
                return {
                    action: 'clear',
                    shouldNotify: false,
                    userMessage: null
                };
            }

            break;

        case 'permission_denied':
            if (msg.includes('Invalid cart token')) {
                clearCartStorage();
                return {
                    action: 'clear',
                    shouldNotify: false,
                    userMessage:
                        "Your cart session is no longer valid. We've started a new cart."
                };
            } else {
                clearCartStorage();
                return {
                    action: 'clear',
                    shouldNotify: false,
                    userMessage: 'Access denied. Starting a new cart.'
                };
            }

        case 'rate_limit_exceeded':
            const retryAfter = details.retry_after || 60;
            return {
                action: 'none',
                shouldNotify: true,
                isRateLimit: true,
                retryAfter,
                userMessage: `You're doing that too fast. Please try again in ${retryAfter} seconds.`
            };

        case 'internal_error':
        case 'database_error':
            return {
                action: 'none',
                shouldNotify: false,
                isServerError: true,
                userMessage:
                    "We're having trouble processing your cart right now. Please try again in a moment."
            };

        default:
            return {
                action: 'none',
                shouldNotify: false,
                userMessage: 'An unexpected error occurred. Please try again.'
            };
    }

    return {
        action: 'none',
        shouldNotify: true,
        userMessage: 'An unexpected error occurred. Please try again.'
    };
};

export const syncCartToStorage = async (
    items,
    discounts,
    maybeTotals = null,
    maybeIdemKey = null,
    maybeCustomer = null
) => {
    const existingCart = getCartFromLocalStorage();
    const {
        cart_id,
        cart_token,
        identified,
        customerInfo,
        shippingInfo,
        prev_items_hash
    } = existingCart || {};

    const persistLocalCart = (nextCart) => {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(nextCart));
    };

    const baseLocalCart = {
        cart_id: cart_id || null,
        cart_token: cart_token || null,
        identified: identified || false,
        items,
        shippingInfo: shippingInfo || {},
        discounts: discounts || [],
        customerInfo: maybeCustomer || customerInfo || {},
        prev_items_hash: prev_items_hash || null
    };

    const customerForBackend = cleanObject(
        maybeCustomer || customerInfo || null
    );

    const headers = {};
    if (maybeIdemKey) {
        headers['Idempotency-Key'] = maybeIdemKey;
    }

    // Helper to persist a cleared cart (preserve items, shippingInfo, discounts, customerInfo)
    const persistClearedLinkage = () => {
        persistLocalCart({
            ...baseLocalCart,
            cart_id: null,
            cart_token: null,
            identified: false,
            prev_items_hash: null
        });
    };

    // Track if we should return an error to the caller
    let errorToReturn = null;

    if (!items || items.length === 0) {
        // keep local empty cart first for UI responsiveness
        persistLocalCart(baseLocalCart);

        if (!cart_id) {
            return;
        }

        const payload = {
            cart_id,
            items: [],
            discount_codes: [],
            ...(prev_items_hash ? { prev_items_hash } : {}),
            ...(maybeTotals ? { totals: toPricing(maybeTotals) } : {}),
            ...(customerForBackend ? { customer: customerForBackend } : {})
        };

        try {
            const response = await persistentCart.update(payload, headers);

            if (response && response.success && response.data) {
                const serverCart = response.data;
                persistLocalCart({
                    ...baseLocalCart,
                    cart_id: serverCart.cart_id || cart_id,
                    cart_token: serverCart.cart_token || cart_token || null,
                    prev_items_hash: serverCart.items_hash || null
                });
            } else if (response && !response.success) {
                const normalizedError = normalizeCartError(
                    response,
                    response.data || response.error
                );

                const actionDescriptor = handleCartError(
                    normalizedError,
                    'syncCartToStorage/empty_update',
                    {
                        isLoggedIn: isLoggedIn(),
                        userEmail:
                            maybeCustomer?.email || customerInfo?.email || null,
                        appToken: getAccessToken()
                    }
                );

                // Capture error for caller if it should be shown to user
                if (actionDescriptor.shouldNotify) {
                    errorToReturn = actionDescriptor;
                }

                if (actionDescriptor.action === 'clear') {
                    // Persist cleared linkage state
                    persistClearedLinkage();
                } else if (
                    actionDescriptor.action === 'refetch_cart' &&
                    actionDescriptor.refetchData
                ) {
                    // Fetch cart from server using cart_id to get cart_token
                    try {
                        const { cart_id: refetchCartId } =
                            actionDescriptor.refetchData;
                        const refetchResponse =
                            await persistentCart.get(refetchCartId);

                        if (
                            refetchResponse &&
                            refetchResponse.success &&
                            refetchResponse.data
                        ) {
                            const freshCart = refetchResponse.data;
                            // Update localStorage with the fetched cart_token
                            persistLocalCart({
                                ...baseLocalCart,
                                cart_id: freshCart.cart_id || refetchCartId,
                                cart_token: freshCart.cart_token || null,
                                items: freshCart.items || [],
                                prev_items_hash: freshCart.items_hash || null
                            });

                            // Retry the original update (cart_token now in localStorage)
                            const retryResponse = await persistentCart.update(
                                payload,
                                headers
                            );

                            if (
                                retryResponse &&
                                retryResponse.success &&
                                retryResponse.data
                            ) {
                                const retryCart = retryResponse.data;
                                persistLocalCart({
                                    ...baseLocalCart,
                                    cart_id: retryCart.cart_id || refetchCartId,
                                    cart_token:
                                        retryCart.cart_token ||
                                        freshCart.cart_token ||
                                        null,
                                    items: retryCart.items || [],
                                    prev_items_hash:
                                        retryCart.items_hash || null
                                });
                            } else {
                            }
                        } else {
                            persistClearedLinkage();
                        }
                    } catch (refetchError) {
                        console.error(
                            '[Cart] Error during refetch_cart (empty cart):',
                            refetchError
                        );
                        persistClearedLinkage();
                    }
                } else if (
                    actionDescriptor.action === 'rehydrate' &&
                    actionDescriptor.rehydrateData
                ) {
                    // Try to rehydrate cart from server using JWT token
                    try {
                        const { email, jwtToken } =
                            actionDescriptor.rehydrateData;
                        const rehydrateResult = await tryJwtIdentityRehydrate(
                            email,
                            jwtToken,
                            customerForBackend
                        );

                        if (rehydrateResult === 'rehydrated') {
                            // Cart is now in localStorage, retry the original update
                            const rehydratedCart = getCartFromLocalStorage();
                            if (
                                rehydratedCart &&
                                rehydratedCart.cart_id &&
                                rehydratedCart.cart_token
                            ) {
                                const retryPayload = {
                                    cart_id: rehydratedCart.cart_id,
                                    items: [],
                                    discount_codes: [],
                                    ...(rehydratedCart.prev_items_hash
                                        ? {
                                              prev_items_hash:
                                                  rehydratedCart.prev_items_hash
                                          }
                                        : {}),
                                    ...(maybeTotals
                                        ? { totals: toPricing(maybeTotals) }
                                        : {}),
                                    ...(customerForBackend
                                        ? { customer: customerForBackend }
                                        : {})
                                };
                                await persistentCart.update(
                                    retryPayload,
                                    headers
                                );
                            }
                        } else {
                            persistClearedLinkage();
                        }
                    } catch (rehydrateError) {
                        console.error(
                            '[Cart] Error during rehydration (empty cart):',
                            rehydrateError
                        );
                        persistClearedLinkage();
                    }
                } else if (
                    actionDescriptor.action === 'refetch' &&
                    actionDescriptor.allowRetry &&
                    cart_id
                ) {
                    // Attempt refetch then retry update (do not rely on items_hash)
                    try {
                        const refetchResponse =
                            await persistentCart.get(cart_id);

                        if (
                            refetchResponse &&
                            refetchResponse.success &&
                            refetchResponse.data
                        ) {
                            const freshCart = refetchResponse.data;

                            // Persist linkage info from server but adopt server items for empty-update flow
                            persistLocalCart({
                                ...baseLocalCart,
                                cart_id: freshCart.cart_id || cart_id,
                                cart_token:
                                    freshCart.cart_token || cart_token || null,
                                items: freshCart.items || [],
                                prev_items_hash: freshCart.items_hash || null
                            });

                            // Retry the update without items_hash (backend doesn't support it yet)
                            const retryResponse = await persistentCart.update(
                                payload,
                                headers
                            );
                            if (
                                retryResponse &&
                                retryResponse.success &&
                                retryResponse.data
                            ) {
                                const retryCart = retryResponse.data;
                                persistLocalCart({
                                    ...baseLocalCart,
                                    cart_id: retryCart.cart_id || cart_id,
                                    cart_token:
                                        retryCart.cart_token ||
                                        cart_token ||
                                        null,
                                    items: retryCart.items || [],
                                    prev_items_hash:
                                        retryCart.items_hash || null
                                });
                            } else {
                            }
                        } else {
                        }
                    } catch (refetchError) {
                        console.error(
                            '[Cart] Error during refetch for empty update:',
                            refetchError
                        );
                    }
                } else {
                    // keep baseLocalCart
                    persistLocalCart(baseLocalCart);
                }
            }
        } catch (error) {
            const normalizedError = normalizeCartError(
                { status: 500 },
                {
                    error: {
                        type: 'internal_error',
                        message:
                            error.message ||
                            'Network error during empty cart sync'
                    }
                }
            );
            const actionDescriptor = handleCartError(
                normalizedError,
                'syncCartToStorage/empty_update'
            );

            // Capture error for caller if it should be shown to user
            if (actionDescriptor.shouldNotify) {
                errorToReturn = actionDescriptor;
            }
        }

        return errorToReturn;
    }

    const normalizedItems = prepareItemsForManager(items);
    const discount_codes = (discounts || [])
        .map((d) => d?.code || d?.id || String(d))
        .filter(Boolean);
    const totalsForBackend = maybeTotals ? toPricing(maybeTotals) : undefined;

    if (cart_id) {
        const payload = {
            cart_id,
            items: normalizedItems,
            discount_codes,
            ...(prev_items_hash ? { prev_items_hash } : {}),
            ...(totalsForBackend ? { totals: totalsForBackend } : {}),
            ...(customerForBackend ? { customer: customerForBackend } : {})
        };

        try {
            const response = await persistentCart.update(payload, headers);

            if (response && response.success && response.data) {
                const serverCart = response.data;
                persistLocalCart({
                    ...baseLocalCart,
                    cart_id: serverCart.cart_id || cart_id,
                    cart_token: serverCart.cart_token || cart_token || null,
                    prev_items_hash: serverCart.items_hash || null
                });
            } else if (response && !response.success) {
                const normalizedError = normalizeCartError(
                    response,
                    response.data || response.error
                );

                const actionDescriptor = handleCartError(
                    normalizedError,
                    'syncCartToStorage/update',
                    {
                        isLoggedIn: isLoggedIn(),
                        userEmail: customerForBackend?.email || null,
                        appToken: getAccessToken()
                    }
                );

                // Capture error for caller if it should be shown to user
                if (actionDescriptor.shouldNotify) {
                    errorToReturn = actionDescriptor;
                }

                if (actionDescriptor.action === 'clear') {
                    // Persist cleared linkage state (preserve items/other info)
                    persistLocalCart({
                        ...baseLocalCart,
                        cart_id: null,
                        cart_token: null,
                        identified: false,
                        prev_items_hash: null
                    });
                } else if (
                    actionDescriptor.action === 'refetch_cart' &&
                    actionDescriptor.refetchData
                ) {
                    // Fetch cart from server using cart_id to get cart_token
                    try {
                        const { cart_id: refetchCartId } =
                            actionDescriptor.refetchData;
                        const refetchResponse =
                            await persistentCart.get(refetchCartId);

                        if (
                            refetchResponse &&
                            refetchResponse.success &&
                            refetchResponse.data
                        ) {
                            const freshCart = refetchResponse.data;
                            // Update localStorage with the fetched cart_token
                            persistLocalCart({
                                ...baseLocalCart,
                                cart_id: freshCart.cart_id || refetchCartId,
                                cart_token: freshCart.cart_token || null,
                                prev_items_hash: freshCart.items_hash || null
                            });

                            // Retry the original update (cart_token now in localStorage)
                            const retryResponse = await persistentCart.update(
                                payload,
                                headers
                            );

                            if (
                                retryResponse &&
                                retryResponse.success &&
                                retryResponse.data
                            ) {
                                const retryCart = retryResponse.data;
                                persistLocalCart({
                                    ...baseLocalCart,
                                    cart_id: retryCart.cart_id || refetchCartId,
                                    cart_token:
                                        retryCart.cart_token ||
                                        freshCart.cart_token ||
                                        null,
                                    prev_items_hash:
                                        retryCart.items_hash || null
                                });
                            } else {
                            }
                        } else {
                            persistLocalCart({
                                ...baseLocalCart,
                                cart_id: null,
                                cart_token: null,
                                identified: false,
                                prev_items_hash: null
                            });
                        }
                    } catch (refetchError) {
                        console.error(
                            '[Cart] Error during refetch_cart:',
                            refetchError
                        );
                        persistLocalCart({
                            ...baseLocalCart,
                            cart_id: null,
                            cart_token: null,
                            identified: false,
                            prev_items_hash: null
                        });
                    }
                } else if (
                    actionDescriptor.action === 'rehydrate' &&
                    actionDescriptor.rehydrateData
                ) {
                    // Try to rehydrate cart from server using JWT token
                    try {
                        const { email, jwtToken } =
                            actionDescriptor.rehydrateData;
                        const rehydrateResult = await tryJwtIdentityRehydrate(
                            email,
                            jwtToken,
                            customerForBackend
                        );

                        if (rehydrateResult === 'rehydrated') {
                            // Cart is now in localStorage, retry the original update
                            const rehydratedCart = getCartFromLocalStorage();
                            if (
                                rehydratedCart &&
                                rehydratedCart.cart_id &&
                                rehydratedCart.cart_token
                            ) {
                                const retryPayload = {
                                    cart_id: rehydratedCart.cart_id,
                                    items: normalizedItems,
                                    discount_codes,
                                    ...(rehydratedCart.prev_items_hash
                                        ? {
                                              prev_items_hash:
                                                  rehydratedCart.prev_items_hash
                                          }
                                        : {}),
                                    ...(totalsForBackend
                                        ? { totals: totalsForBackend }
                                        : {}),
                                    ...(customerForBackend
                                        ? { customer: customerForBackend }
                                        : {})
                                };
                                await persistentCart.update(
                                    retryPayload,
                                    headers
                                );
                            }
                        } else {
                            persistLocalCart({
                                ...baseLocalCart,
                                cart_id: null,
                                cart_token: null,
                                identified: false,
                                prev_items_hash: null
                            });
                        }
                    } catch (rehydrateError) {
                        console.error(
                            '[Cart] Error during rehydration:',
                            rehydrateError
                        );
                        persistLocalCart({
                            ...baseLocalCart,
                            cart_id: null,
                            cart_token: null,
                            prev_items_hash: null,
                            identified: false
                        });
                    }
                } else if (
                    actionDescriptor.action === 'refetch' &&
                    actionDescriptor.allowRetry &&
                    cart_id
                ) {
                    // Refetch server linkage (cart_id/cart_token) but preserve local items.
                    // Previous implementation used items_hash for conditional retries; that logic has been
                    // removed. We still perform a refetch to obtain fresh linkage and then attempt an
                    // unconditional retry of the original update, preserving local items.
                    try {
                        const refetchResponse =
                            await persistentCart.get(cart_id);

                        if (
                            refetchResponse &&
                            refetchResponse.success &&
                            refetchResponse.data
                        ) {
                            const freshCart = refetchResponse.data;

                            // Persist linkage info from server but keep local items intact
                            persistLocalCart({
                                ...baseLocalCart,
                                cart_id: freshCart.cart_id || cart_id,
                                cart_token:
                                    freshCart.cart_token || cart_token || null,
                                prev_items_hash: freshCart.items_hash || null
                                // Note: items intentionally left as baseLocalCart.items
                            });

                            // Retry the original update without items_hash (backend doesn't support it yet)
                            const retryResponse = await persistentCart.update(
                                payload,
                                headers
                            );

                            if (
                                retryResponse &&
                                retryResponse.success &&
                                retryResponse.data
                            ) {
                                const retryCart = retryResponse.data;
                                persistLocalCart({
                                    ...baseLocalCart,
                                    cart_id: retryCart.cart_id || cart_id,
                                    cart_token:
                                        retryCart.cart_token ||
                                        cart_token ||
                                        null,
                                    prev_items_hash:
                                        retryCart.items_hash || null
                                });
                            }
                        } else {
                            persistLocalCart(baseLocalCart);
                        }
                    } catch (refetchError) {
                        console.error(
                            '[Cart] Error during refetch:',
                            refetchError
                        );
                        persistLocalCart(baseLocalCart);
                    }
                } else {
                    // default behavior: persist local best-effort state
                    persistLocalCart(baseLocalCart);
                }
            }
        } catch (error) {
            const normalizedError = normalizeCartError(
                { status: 500 },
                {
                    error: {
                        type: 'internal_error',
                        message:
                            error.message || 'Network error during cart sync'
                    }
                }
            );
            const actionDescriptor = handleCartError(
                normalizedError,
                'syncCartToStorage/update',
                {
                    isLoggedIn: isLoggedIn(),
                    userEmail:
                        customerForBackend?.email ||
                        customerInfo?.email ||
                        null,
                    appToken: getAccessToken()
                }
            );

            // Capture error for caller if it should be shown to user
            if (actionDescriptor.shouldNotify) {
                errorToReturn = actionDescriptor;
            }

            persistLocalCart(baseLocalCart);
        }

        return errorToReturn;
    }

    const createIdemKey = maybeIdemKey || uuidV4();

    try {
        const response = await persistentCart.create(
            normalizedItems,
            totalsForBackend ||
                toPricing({
                    subTotal: maybeTotals?.subTotal ?? 0,
                    tax: maybeTotals?.tax ?? 0,
                    shipping: maybeTotals?.shipping ?? 0,
                    discount: maybeTotals?.discount ?? 0,
                    grandTotal: maybeTotals?.grandTotal ?? 0
                }),
            customerForBackend,
            createIdemKey
        );

        if (response && response.success && response.data) {
            const serverCart = response.data;
            persistLocalCart({
                ...baseLocalCart,
                cart_id: serverCart.cart_id,
                cart_token: serverCart.cart_token || null,
                identified: !!customerForBackend?.primary_email,
                prev_items_hash: serverCart.items_hash || null
            });
        } else if (response && !response.success) {
            const normalizedError = normalizeCartError(
                response,
                response.data || response.error
            );
            const actionDescriptor = handleCartError(
                normalizedError,
                'syncCartToStorage/create'
            );

            // Capture error for caller if it should be shown to user
            if (actionDescriptor.shouldNotify) {
                errorToReturn = actionDescriptor;
            }

            if (actionDescriptor.action === 'clear') {
                // Persist cleared linkage state
                persistClearedLinkage();
            } else {
                // default: keep empty linkage (create failed)
                persistLocalCart({
                    ...baseLocalCart,
                    cart_id: null,
                    cart_token: null,
                    identified: !!customerForBackend?.primary_email
                });
            }
        }
    } catch (error) {
        const normalizedError = normalizeCartError(
            { status: 500 },
            {
                error: {
                    type: 'internal_error',
                    message:
                        error.message || 'Network error during cart creation'
                }
            }
        );
        const actionDescriptor = handleCartError(
            normalizedError,
            'syncCartToStorage/create',
            {
                isLoggedIn: isLoggedIn(),
                userEmail: customerForBackend?.email || null,
                appToken: getAccessToken()
            }
        );

        // Capture error for caller if it should be shown to user
        if (actionDescriptor.shouldNotify) {
            errorToReturn = actionDescriptor;
        }

        persistLocalCart({
            ...baseLocalCart,
            cart_id: null,
            cart_token: null,
            identified: !!customerForBackend?.primary_email
        });
    }

    // Return error if one should be shown to the user
    return errorToReturn;
};

export const addItemToCart = async (
    serviceId,
    location,
    currentItems,
    customerId,
    discounts,
    displayName = null,
    addOns = null,
    giftCardValue = null,
    vehicleID = null,
    garageID = null,
    quantity = 1,
    giftCardQuantity = 1,
    addPromo = false,
    slug = null,
    isPhysicalGiftCard = false
) => {
    let newItems = [...currentItems];
    let item = await queryCartItem(
        serviceId,
        location,
        displayName,
        giftCardValue,
        addPromo,
        slug
    );

    let totalsResult;

    if (item) {
        const today = new Date().toISOString();
        if (addOns) {
            item.addOns = addOns;
        }
        if (giftCardValue) {
            item.giftCardQuantity = giftCardQuantity;
        }
        if (vehicleID) {
            if (item.productType === 'subscription') {
                remove(newItems, {
                    productType: 'subscription',
                    vehicle: {
                        vehicleId: vehicleID.toString(),
                        garage: garageID
                    }
                });
            }
            item.vehicle = {
                vehicleId: vehicleID.toString(),
                garage: garageID
            };
        }

        const existingItemIndex = findIndex(newItems, (newItem) => {
            if (newItem.databaseId !== item.databaseId) {
                return false;
            }

            if (item.productType === 'gift-card') {
                return (
                    newItem.giftCardValue === giftCardValue &&
                    !!newItem.isPhysicalGiftCard === !!isPhysicalGiftCard
                );
            }

            return true;
        });
        let newItem;
        if (
            item.productType !== 'subscription' &&
            existingItemIndex > -1 &&
            !item.addOns
        ) {
            newItem = {
                ...newItems[existingItemIndex],
                quantity: newItems[existingItemIndex].quantity + quantity
            };
            newItems[existingItemIndex] = newItem;
        } else {
            const addOns = map(item.addOns, (addOn) => ({
                ...addOn,
                priceDisplay: addOn.useBells
                    ? `${addOn.bellsCost} Bells`
                    : formatPrice(addOn.price)
            }));
            newItem = {
                ...item,
                addOns,
                quantity,
                giftCardValue,
                isPhysicalGiftCard:
                    item.productType === 'gift-card'
                        ? isPhysicalGiftCard
                        : undefined,
                cartItemId: Date.now().toString(),
                priceDisplay: formatPrice(item.price)
            };
            newItems = [...newItems, newItem];
        }

        if (addPromo && item.bogoPrepaidPromotion) {
            if (
                item.bogoPrepaidPromotion.effectiveFrom <= today &&
                item.bogoPrepaidPromotion.effectiveTo >= today
            ) {
                newItems = getBogoItems(
                    item.bogoPrepaidPromotion.purchaseQuantity,
                    item.bogoPrepaidPromotion.percentOff ? 'percent' : 'dollar',
                    item.bogoPrepaidPromotion.percentOff
                        ? item.bogoPrepaidPromotion.percentOff
                        : item.bogoPrepaidPromotion.price,
                    newItem.quantity,
                    newItem.cartItemId,
                    newItems
                );
            } else {
                newItem.bogoPrepaidPromotion = undefined;
            }
        } else if (!addPromo || !item.bogoPrepaidPromotion) {
            if (!addPromo && item.bogoPrepaidPromotion) {
                newItem.bogoPrepaidPromotion = undefined;
                item.bogoPrepaidPromotion = undefined;
            }
            newItems = getBogoItems(
                item.bogoThreshold,
                item.bogoDiscountType,
                item.bogoDiscountAmount,
                newItem.quantity,
                newItem.cartItemId,
                newItems
            );
        }

        totalsResult = await getTotals(newItems, customerId, discounts);

        let customer = null;
        if (isLoggedIn()) {
            try {
                const user = await getUser();
                if (user && user.email && !user.error) {
                    customer = cleanObject({
                        source: 'web',
                        stripe_customer_id: user.stripe_customer_id,
                        name: user.name,
                        primary_email: user.email,
                        phone: user.phone_number
                    });
                }
            } catch (error) {
                console.error(
                    'Error fetching user during addItemToCart:',
                    error
                );
            }
        }

        const idemKey = uuidV4();
        const totals = {
            subTotal: totalsResult.subTotal,
            tax: totalsResult.tax,
            shipping: totalsResult.shipping,
            discount: totalsResult.discount,
            grandTotal: totalsResult.grandTotal
        };

        const cartError = await syncCartToStorage(
            totalsResult.items,
            discounts,
            totals,
            idemKey,
            customer
        );

        createGtagEcommEvent('add_to_cart', [newItem]);

        // Return cart error if one occurred
        if (cartError) {
            const cart = getCartFromLocalStorage();
            return {
                cart_id: cart?.cart_id || null,
                items: totalsResult?.items || newItems,
                totals: {
                    subTotal: totalsResult?.subTotal || 0,
                    tax: totalsResult?.tax || 0,
                    shipping: totalsResult?.shipping || 0,
                    grandTotal: totalsResult?.grandTotal || 0,
                    discount: totalsResult?.discount || 0,
                    giftCardBalance: totalsResult?.giftCardBalance || 0
                },
                discounts: discounts || [],
                error: cartError
            };
        }
    }

    const cart = getCartFromLocalStorage();

    return {
        cart_id: cart?.cart_id || null,
        items: totalsResult?.items || newItems,
        totals: {
            subTotal: totalsResult?.subTotal || 0,
            tax: totalsResult?.tax || 0,
            shipping: totalsResult?.shipping || 0,
            grandTotal: totalsResult?.grandTotal || 0,
            discount: totalsResult?.discount || 0,
            giftCardBalance: totalsResult?.giftCardBalance || 0
        },
        discounts: discounts || []
    };
};

export const removeItemFromCart = async (
    cartItemId,
    currentItems,
    customerId,
    discounts
) => {
    let removeItemIds = isArray(cartItemId) ? cartItemId : [cartItemId];
    for (const id of removeItemIds) {
        await washes.deleteInvoiceById(id, currentItems);
    }

    const removeBogoItems = filter(currentItems, ({ cartItemId }) =>
        find(removeItemIds, (removeCartItemId) =>
            startsWith(cartItemId, `${removeCartItemId}-bogo`)
        )
    );

    removeItemIds = [...removeItemIds, ...map(removeBogoItems, 'cartItemId')];

    const newItems = filter(
        currentItems,
        ({ cartItemId: itemID }) => !includes(removeItemIds, itemID)
    );

    const {
        items,
        subTotal,
        tax,
        shipping,
        grandTotal,
        discount,
        giftCardBalance
    } = await getTotals(newItems, customerId, discounts);

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        discounts,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    const removeItems = filter(currentItems, ({ cartItemId }) => {
        return find(removeItemIds, (removeCartItemId) => {
            return cartItemId === removeCartItemId;
        });
    });
    createGtagEcommEvent('remove_from_cart', removeItems);

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    return {
        cart_id,
        items,
        discounts,
        totals: {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount,
            giftCardBalance
        },
        error: cartError
    };
};

export const updateCartItemAddOns = async (
    cartItemId,
    addOns,
    currentItems,
    location,
    customerId,
    discounts
) => {
    let newItems = [...currentItems];
    const newItemIndex = findIndex(newItems, {
        cartItemId
    });
    const originalItem = newItems[newItemIndex];

    // Check if the original item has a valid prepaid promotion within date range
    const today = new Date().toISOString();
    const hasValidPromotion =
        originalItem.bogoPrepaidPromotion &&
        originalItem.bogoPrepaidPromotion.effectiveFrom &&
        originalItem.bogoPrepaidPromotion.effectiveTo &&
        originalItem.bogoPrepaidPromotion.effectiveFrom <= today &&
        originalItem.bogoPrepaidPromotion.effectiveTo >= today;

    const newItem = await queryCartItem(
        originalItem.serviceId,
        location,
        originalItem.displayName ||
            (originalItem.productType === 'digital'
                ? originalItem.title
                : null),
        originalItem.giftCardValue,
        hasValidPromotion,
        originalItem.slug || null
    );
    if (newItem) {
        // Preserve the original recipients data if promotion exists
        if (hasValidPromotion && originalItem.recipients) {
            newItem.recipients = originalItem.recipients;
            newItem.hasRecipient = originalItem.hasRecipient;
        }

        const newAddons = map(addOns, (addOn) => ({
            ...addOn,
            priceDisplay: addOn.useBells
                ? `${addOn.bellsCost} Bells`
                : formatPrice(addOn.price)
        }));
        const addedAddons = differenceBy(
            newAddons,
            newItems[newItemIndex].addOns,
            'id'
        );
        const removedAddons = differenceBy(
            newItems[newItemIndex].addOns,
            newAddons,
            'id'
        );
        if (addedAddons.length) {
            createGtagEcommEvent('add_to_cart', addedAddons);
        }
        if (removedAddons.length) {
            createGtagEcommEvent('remove_from_cart', removedAddons);
        }
        newItem.addOns = newAddons;
        newItems[newItemIndex] = {
            ...newItems[newItemIndex],
            ...newItem,
            priceDisplay: formatPrice(newItem.price)
        };
    } else {
        remove(newItems, ({ cartItemId: itemID }) =>
            itemID.includes(cartItemId)
        );
    }

    const {
        items,
        subTotal,
        tax,
        shipping,
        grandTotal,
        discount,
        giftCardBalance
    } = await getTotals(newItems, customerId, discounts);

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        discounts,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    return {
        cart_id,
        items,
        discounts,
        totals: {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount,
            giftCardBalance
        },
        error: cartError
    };
};

export const updateAddOnPaymentMethod = async (
    cartItemId,
    addOnId,
    useBells,
    currentItems,
    customerId,
    discounts
) => {
    let newItems = [...currentItems];
    const itemIndex = findIndex(newItems, { cartItemId });

    if (itemIndex === -1) {
        return {
            items: currentItems,
            discounts,
            totals: {},
            error: { message: 'Cart item not found' }
        };
    }

    const item = newItems[itemIndex];
    if (!item.addOns || !isArray(item.addOns)) {
        return {
            items: currentItems,
            discounts,
            totals: {},
            error: { message: 'No add-ons found on cart item' }
        };
    }

    const addOnIndex = findIndex(item.addOns, { id: addOnId });
    if (addOnIndex === -1) {
        return {
            items: currentItems,
            discounts,
            totals: {},
            error: { message: 'Add-on not found' }
        };
    }

    const updatedAddOns = [...item.addOns];
    updatedAddOns[addOnIndex] = {
        ...updatedAddOns[addOnIndex],
        useBells,
        priceDisplay: useBells
            ? `${updatedAddOns[addOnIndex].bellsCost} Bells`
            : formatPrice(updatedAddOns[addOnIndex].price)
    };

    newItems[itemIndex] = {
        ...item,
        addOns: updatedAddOns
    };

    const {
        items,
        subTotal,
        tax,
        shipping,
        grandTotal,
        discount,
        giftCardBalance
    } = await getTotals(newItems, customerId, discounts);

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        discounts,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    return {
        cart_id,
        items,
        discounts,
        totals: {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount,
            giftCardBalance
        },
        error: cartError
    };
};

export const updateCartPromoCode = async (
    promoCode,
    currentCodes,
    currentItems,
    customerId
) => {
    const newItems = [...currentItems];

    const validation = await validatePromoCode(promoCode);

    if (!validation.success) {
        const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
        const { cart_id } = cart || {};
        return {
            success: false,
            cart_id,
            discounts: currentCodes,
            promoCodeErrorMsg: validation.error || 'Invalid promo code'
        };
    }

    const promoCodeData = validation.promoCode;

    const promoCodes = [
        ...currentCodes,
        {
            code: promoCode,
            discount_type: promoCodeData.discount_type,
            discount_amount: promoCodeData.discount_amount,
            discount_percentage: promoCodeData.discount_percentage,
            applicable_to: promoCodeData.applicable_to,
            stripe_promotion_code_id:
                promoCodeData.stripe_promotion_code_id ?? null,
            max_total_redemptions: promoCodeData.max_total_redemptions ?? null,
            max_redemptions_per_user:
                promoCodeData.max_redemptions_per_user ?? null,
            redemption_count: promoCodeData.redemption_count ?? 0,
            appliedToItemIds: [],
            notAppliedReason: null
        }
    ];

    const {
        items,
        subTotal,
        tax,
        shipping,
        grandTotal,
        discount,
        giftCardBalance,
        promoCodeErrors,
        promoCodes: newPromoCodes,
        promoCodeErrorMsg
    } = await getTotals(newItems, customerId, promoCodes);

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    if (promoCodeErrors.length) {
        return {
            success: false,
            cart_id,
            items,
            totals: {
                subTotal,
                tax,
                shipping,
                grandTotal,
                discount,
                giftCardBalance
            },
            discounts: currentCodes,
            promoCodeErrorMsg
        };
    }

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        newPromoCodes,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    return {
        success: true,
        cart_id,
        items,
        totals: {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount,
            giftCardBalance
        },
        discounts: newPromoCodes,
        error: cartError
    };
};

export const removePromoCode = async (
    promoCode,
    currentItems,
    customerId,
    discounts
) => {
    const newDiscounts = filter(discounts, ({ code }) => code !== promoCode);
    const newItems = [...currentItems];
    for (const item of newItems) {
        if (item.promoCode && item.promoCode.code === promoCode) {
            delete item.promoCode;
        }
    }

    const {
        items,
        subTotal,
        tax,
        shipping,
        giftCardBalance,
        grandTotal,
        discount
    } = await getTotals(newItems, customerId, newDiscounts);

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        newDiscounts,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    return {
        cart_id,
        items,
        totals: {
            subTotal,
            tax,
            shipping,
            giftCardBalance,
            grandTotal,
            discount
        },
        discounts: newDiscounts,
        error: cartError
    };
};

export const removeCartItemAddOn = async (
    cartItemId,
    addOnId,
    currentItems,
    customerId,
    discounts
) => {
    const newItems = [...currentItems];

    const itemIndex = findIndex(newItems, {
        cartItemId: cartItemId
    });

    const removedAddons = remove(newItems[itemIndex].addOns, { id: addOnId });

    const {
        items,
        subTotal,
        tax,
        shipping,
        giftCardBalance,
        grandTotal,
        discount,
        promoCodes
    } = await getTotals(newItems, customerId, discounts);

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        discounts,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    createGtagEcommEvent('remove_from_cart', removedAddons);

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    return {
        cart_id,
        items,
        totals: {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount,
            giftCardBalance
        },
        discounts: promoCodes,
        error: cartError
    };
};

const updateVehicleInfoQuantity = (cartItemId, cartItems, quantity) => {
    const itemIndex = findIndex(cartItems, { cartItemId });
    const item = cartItems[itemIndex];
    const vehicleInformation = item?.vehicleInformation || [];

    if (isEmpty(vehicleInformation)) {
        return [...cartItems];
    }

    const vehicleInfoQuantity = vehicleInformation.length;

    if (vehicleInfoQuantity > quantity) {
        const info = vehicleInformation.slice(0, vehicleInfoQuantity - 1);
        const newCartItems = [...cartItems];
        newCartItems[itemIndex].vehicleInformation = info;
        return newCartItems;
    } else {
        const info = vehicleInformation[vehicleInfoQuantity - 1];
        const id = `${vehicleInfoQuantity}-${cartItemId}`;
        const newCartItems = [...cartItems];
        newCartItems[itemIndex].vehicleInformation.push({ ...info, id });
        return newCartItems;
    }
};

export const updateCartItemQuantity = async (
    cartItemId,
    quantity,
    currentItems,
    location,
    customerId,
    discounts
) => {
    let newItems = updateVehicleInfoQuantity(
        cartItemId,
        currentItems,
        quantity
    );

    const itemIndex = findIndex(newItems, { cartItemId });

    const newItem = await queryCartItem(
        newItems[itemIndex].serviceId,
        location,
        newItems[itemIndex].displayName ||
            (newItems[itemIndex].productType === 'digital'
                ? newItems[itemIndex].title
                : null),
        newItems[itemIndex].giftCardValue,
        false,
        newItems[itemIndex].slug || null
    );

    if (newItem) {
        const quantityDiff = quantity - newItem.quantity;
        newItem.quantity = quantity;
        newItem.cartItemId = cartItemId;

        // check for deals/promos
        if (newItem.bogoPrepaidPromotion) {
            // item is a prepaid promotional wash
            newItems = getBogoItems(
                newItem.bogoPrepaidPromotion.purchaseQuantity,
                newItem.bogoPrepaidPromotion.percentOff ? 'percent' : 'dollar',
                newItem.bogoPrepaidPromotion.percentOff
                    ? newItem.bogoPrepaidPromotion.percentOff
                    : newItem.bogoPrepaidPromotion.price,
                newItem.quantity,
                newItem.cartItemId,
                newItems
            );
        } else if (!newItem.bogoPrepaidPromotion) {
            // item is a standard website bogo
            newItems = getBogoItems(
                newItem.bogoThreshold,
                newItem.bogoDiscountType,
                newItem.bogoDiscountAmount,
                newItem.quantity,
                newItem.cartItemId,
                newItems
            );
        }

        newItems[itemIndex] = {
            ...newItems[itemIndex],
            ...newItem,
            priceDisplay: formatPrice(newItem.price)
        };
        if (quantityDiff > 0) {
            createGtagEcommEvent(
                'add_to_cart',
                [
                    {
                        ...newItem,
                        quantity: quantityDiff
                    }
                ],
                quantityDiff
            );
        } else if (quantityDiff < 0) {
            createGtagEcommEvent(
                'remove_from_cart',
                [
                    {
                        ...newItem,
                        quantity: -quantityDiff
                    }
                ],
                quantityDiff
            );
        }
    } else {
        remove(newItems, ({ cartItemId: itemID }) =>
            itemID.includes(cartItemId)
        );
    }

    const {
        items,
        subTotal,
        tax,
        shipping,
        giftCardBalance,
        grandTotal,
        discount,
        promoCodes
    } = await getTotals(newItems, customerId, discounts);

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        discounts,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    return {
        cart_id,
        items,
        totals: {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount,
            giftCardBalance
        },
        discounts: promoCodes,
        error: cartError
    };
};

export const updateCartItemPrice = async (
    cartItemId,
    newPrice,
    currentItems,
    customerId,
    discounts
) => {
    const newItems = [...currentItems];
    const itemIndex = findIndex(newItems, { cartItemId });
    newItems[itemIndex].price = newPrice;
    newItems[itemIndex].priceDisplay = formatPrice(newPrice);

    const {
        items,
        subTotal,
        tax,
        shipping,
        giftCardBalance,
        grandTotal,
        discount,
        promoCodes
    } = await getTotals(newItems, customerId, discounts);

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        discounts,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    return {
        cart_id,
        items,
        totals: {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount,
            giftCardBalance
        },
        discounts: promoCodes,
        error: cartError
    };
};

export const repriceCartItem = async (item, location) => {
    const newItem = await queryCartItem(
        item.serviceId,
        location,
        item.displayName ||
            (item.productType === 'digital' ? item.title : null),
        item.giftCardValue,
        false,
        item.slug || null
    );

    if (!newItem) {
        return null;
    }

    const existingAddOns = item.addOns || [];
    const recalculatedAddOns = existingAddOns.map((addOn) => {
        const newPrice =
            find(addOn.addOnPrices, { priceGroup: location.priceGroup })
                ?.unitAmount ?? addOn.price;
        const newBellsCost = getAddOnBellsCost(
            { ...addOn, price: newPrice },
            location.priceGroup
        );
        return {
            ...addOn,
            price: newPrice,
            bellsCost: newBellsCost,
            priceDisplay: addOn.useBells
                ? `${newBellsCost} Bells`
                : formatPrice(newPrice)
        };
    });

    return {
        ...item,
        ...newItem,
        addOns: recalculatedAddOns,
        priceDisplay: formatPrice(newItem.price)
    };
};

export const repriceCartItems = async (items, locations) => {
    if (!Array.isArray(items) || !Array.isArray(locations)) {
        return items;
    }

    const result = [];
    for (const item of items) {
        if (item.productType !== 'digital') {
            result.push(item);
            continue;
        }
        const fullLocation = find(locations, {
            locationId: item.location?.id
        });
        if (!fullLocation) {
            result.push(item);
            continue;
        }
        const repriced = await repriceCartItem(item, fullLocation);
        if (repriced) {
            result.push(repriced);
        }
    }
    return result;
};

export const updateCartItemLocation = async (
    cartItemId,
    location,
    currentItems,
    customerId,
    discounts
) => {
    let newItems = [...currentItems];
    const itemIndex = findIndex(newItems, { cartItemId });
    const repriced = await repriceCartItem(newItems[itemIndex], location);

    if (repriced) {
        newItems[itemIndex] = repriced;
    } else {
        remove(newItems, ({ cartItemId: itemID }) =>
            itemID.includes(cartItemId)
        );
    }

    const {
        items,
        subTotal,
        tax,
        shipping,
        giftCardBalance,
        grandTotal,
        discount,
        promoCodes
    } = await getTotals(newItems, customerId, discounts);

    const idemKey = uuidV4();
    const cartError = await syncCartToStorage(
        items,
        discounts,
        {
            subTotal,
            tax,
            shipping,
            grandTotal,
            discount
        },
        idemKey
    );

    const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
    const { cart_id } = cart || {};

    return {
        cart_id,
        items,
        totals: {
            subTotal,
            tax,
            shipping,
            giftCardBalance,
            grandTotal,
            discount
        },
        discounts: promoCodes,
        error: cartError
    };
};

export const initializeLocalCart = (customerInfo) => {
    const freshCart = {
        cart_id: null,
        cart_token: null,
        identified: false,
        items: [],
        shippingInfo: {},
        discounts: [],
        customerInfo: customerInfo || {}
    };

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(freshCart));
    return freshCart;
};

export const getCartFromLocalStorage = () => {
    try {
        const cart = localStorage.getItem(CART_STORAGE_KEY);
        return cart ? JSON.parse(cart) : null;
    } catch (e) {
        console.error('Failed to parse cart from localStorage:', e);
        return null;
    }
};

const tryJwtIdentityRehydrate = async (
    email,
    jwtToken,
    customerInfo,
    locations
) => {
    try {
        const response = await persistentCart.identifyWithJwtToken(
            email,
            jwtToken
        );

        if (
            response.authorized === true &&
            response.status === 'linked_existing' &&
            response.cart
        ) {
            const serverCart = response.cart;
            const repricedItems = await repriceCartItems(
                serverCart.items || [],
                locations
            );
            const localCart = {
                cart_id: serverCart.cart_id,
                cart_token: serverCart.cart_token || null,
                identified: !!serverCart.customer?.primary_email,
                items: repricedItems,
                shippingInfo: serverCart.shippingInfo || {},
                discounts: serverCart.discounts || [],
                customerInfo
            };

            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(localCart));
            return 'rehydrated';
        }

        return 'no_cart';
    } catch (e) {
        console.error('JWT identity rehydrate failed:', e);
        return 'error';
    }
};

export const handleLoginCartSync = async (userData, locations = []) => {
    // Check if user explicitly cleared their cart
    const cartClearedByUser = sessionStorage.getItem('cart_cleared_by_user');
    if (cartClearedByUser === 'true') {
        // Don't remove the flag yet - let it persist for a bit to prevent race conditions
        setTimeout(() => {
            sessionStorage.removeItem('cart_cleared_by_user');
        }, 1000);

        const customerInfo = {
            stripe_customer_id: userData.stripe_customer_id,
            name: userData.name,
            email: userData.email,
            phone: userData.phone_number
        };
        initializeLocalCart(customerInfo);
        return;
    }

    const customerInfo = {
        stripe_customer_id: userData.stripe_customer_id,
        name: userData.name,
        email: userData.email,
        phone: userData.phone_number
    };

    const cart = getCartFromLocalStorage();
    const appToken = getAccessToken();

    if (!cart) {
        if (userData.email && appToken) {
            const rehydrateResult = await tryJwtIdentityRehydrate(
                userData.email,
                appToken,
                customerInfo,
                locations
            );
            if (rehydrateResult === 'rehydrated') {
                return;
            }
        }
        initializeLocalCart(customerInfo);
        return;
    }

    const hasLocalItems = Array.isArray(cart.items) && cart.items.length > 0;

    if (hasLocalItems) {
        let identified = false;
        let needsBackendSync = false;

        const cartEmail = cart.customerInfo?.email;
        const userEmail = userData.email;
        const hasEmailMismatch =
            cart.recoveredWithEmail &&
            cartEmail &&
            userEmail &&
            cartEmail.toLowerCase() !== userEmail.toLowerCase();

        if (hasEmailMismatch) {
            sessionStorage.setItem(
                'cart_email_mismatch',
                JSON.stringify({
                    cartEmail,
                    userEmail,
                    cartId: cart.cart_id,
                    timestamp: Date.now()
                })
            );
            return;
        }

        if (cart.cart_id && userData.email && !cart.identified) {
            try {
                const identifyResponse = await persistentCart.identify(
                    cart.cart_id,
                    userData.email,
                    false
                );

                if (
                    identifyResponse &&
                    identifyResponse.success &&
                    identifyResponse.data
                ) {
                    identified = true;
                    needsBackendSync = true;
                } else if (identifyResponse && !identifyResponse.success) {
                    const normalizedError = normalizeCartError(
                        identifyResponse,
                        identifyResponse.data || identifyResponse.error
                    );
                    handleCartError(
                        normalizedError,
                        'persistentCart.identify in handleLoginCartSync'
                    );
                }
            } catch (error) {
                const normalizedError = normalizeCartError(
                    { status: 500 },
                    {
                        error: {
                            type: 'internal_error',
                            message:
                                error.message ||
                                'Network error during cart identification'
                        }
                    }
                );
                handleCartError(
                    normalizedError,
                    'persistentCart.identify in handleLoginCartSync'
                );
            }
        }

        // NOTE: Recovered carts with an existing email are treated as identified.
        // Preserve the cart's original email to prevent overwriting when user logs in with a different account.
        // This only applies to carts that were explicitly recovered with an email (recoveredWithEmail flag).
        // Normal account switching (logout/login) should update the email as usual.
        const updatedCart = {
            cart_id: cart.cart_id,
            cart_token: cart.cart_token || null,
            identified: identified || cart.identified || false,
            recoveredWithEmail: cart.recoveredWithEmail || false,
            items: cart.items || [],
            shippingInfo: cart.shippingInfo || {},
            discounts: cart.discounts || [],
            customerInfo:
                cart.recoveredWithEmail && cart.customerInfo?.email
                    ? {
                          ...customerInfo,
                          email: cart.customerInfo.email
                      }
                    : customerInfo
        };

        if (updatedCart.items.length > 0) {
            try {
                updatedCart.items = await repriceCartItems(
                    updatedCart.items,
                    locations
                );
                const totalInfo = await getTotals(
                    updatedCart.items,
                    customerInfo.stripe_customer_id,
                    updatedCart.discounts
                );

                if (needsBackendSync) {
                    await syncCartToStorage(
                        totalInfo.items,
                        totalInfo.promoCodes || updatedCart.discounts,
                        {
                            subTotal: totalInfo.subTotal,
                            tax: totalInfo.tax,
                            shipping: totalInfo.shipping,
                            grandTotal: totalInfo.grandTotal,
                            discount: totalInfo.discount
                        },
                        null,
                        {
                            stripe_customer_id: userData.stripe_customer_id,
                            name: userData.name,
                            email: userData.email,
                            phone: userData.phone_number
                        }
                    );
                }

                localStorage.setItem(
                    CART_STORAGE_KEY,
                    JSON.stringify({
                        cart_id: updatedCart.cart_id,
                        cart_token: updatedCart.cart_token,
                        identified: updatedCart.identified,
                        items: totalInfo.items,
                        shippingInfo: updatedCart.shippingInfo,
                        discounts:
                            totalInfo.promoCodes || updatedCart.discounts,
                        customerInfo: updatedCart.customerInfo
                    })
                );
            } catch (error) {
                console.error(
                    'Failed to recalculate totals or sync cart after login:',
                    error
                );
                localStorage.setItem(
                    CART_STORAGE_KEY,
                    JSON.stringify(updatedCart)
                );
            }
        } else {
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedCart));
        }
        return;
    }

    if (userData.email && appToken) {
        const rehydrateResult = await tryJwtIdentityRehydrate(
            userData.email,
            appToken,
            customerInfo,
            locations
        );
        if (rehydrateResult === 'rehydrated') {
            return;
        }
    }

    const emptyCart = {
        cart_id: cart.cart_id || null,
        cart_token: cart.cart_token || null,
        identified: cart.identified || false,
        items: [],
        shippingInfo: cart.shippingInfo || {},
        discounts: cart.discounts || [],
        customerInfo
    };

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(emptyCart));
};
